/* eslint-disable react/prop-types */
import { Trans } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import Moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCircleCheck,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import ScreeningService from '../../../../../Services/ScreeningService';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';
import DataPanel from '../../../../../Components/DataPanel/DataPanel';
import AuthContext from '../../../../../Context/AuthContext';
import './CrimeRecordView.scss';

const shouldUnescape = true;
const tOptions = Object.freeze({
  returnObjects: true,
  joinArrays: '',
});

const gT = (t, key) =>
  t(`company-info-modules.regulatory.crime-record-view${key}`);

const gsT = (t, key) => t(`shared${key}`);

function ResultEntry({ entry, index, onDownloadClick, t }) {
  return (
    <div className="list-entry">
      <p>{`${index + 1}.`}</p>
      <p className="s-text breakable">
        <b>{gT(t, '.result-entry.category')}&nbsp;</b>
        {entry.shelf}
      </p>
      <p className="s-text">
        <b>{gT(t, '.result-entry.document-type')}&nbsp;</b>
        {entry.docType}
      </p>
      <XpektorButton
        content={
          entry.downloadable
            ? gT(t, '.result-entry.xpektor-btn.fetch-documents')
            : gT(t, '.result-entry.xpektor-btn.not-downloadable')
        }
        onClick={() => {
          if (entry.downloadable) {
            onDownloadClick();
          }
        }}
        disabled={!entry.downloadable}
      />
    </div>
  );
}

function ScreeningEntry({ entry, onClick, selected, t }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      className={`list-entry ${selected ? 'selected-entry' : ''} ${
        isHovered && !selected ? 'hovered-entry' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <p className="s-text">
        <b>
          {entry.organizationNumber
            ? gT(t, '.screening-entry.organization')
            : entry.name
            ? gT(t, '.screening-entry.person')
            : gsT(t, '.labels.not-avaialable')}
        </b>
      </p>
      <p>{entry.name ?? gsT(t, '.labels.not-avaialable')}</p>
      {entry.results.length > 0 ? (
        <div className="icon-text-row">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="icon warning"
          />
          <p>{`${entry.results.length} ${gT(
            t,
            '.screening-entry.suffix-results'
          )}`}</p>
        </div>
      ) : (
        <div className="icon-text-row">
          <FontAwesomeIcon icon={faCircleCheck} className="icon ok" />
          <p>{gT(t, '.screening-entry.no-results')}</p>
        </div>
      )}
    </button>
  );
}

function RecordEntry({ entry, onClick, selected, t }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      type="button"
      className={`list-entry ${selected ? 'selected-entry' : ''} ${
        isHovered && !selected ? 'hovered-entry' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <p>
        <b>
          {gT(t, '.record-entry.prefix-scan-date')} -&nbsp;
          {Moment.utc(entry.screeningDate).local().format('YYYY-MM-DD HH:mm')}
        </b>
      </p>

      {entry.crimeScreenings.length === 0 ? (
        <div className="icon-text-row">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="icon error"
          />
          <p>{gT(t, '.record-entry.general-error')}</p>
        </div>
      ) : (
        <p>{`${gT(t, '.record-entry.prefix-result')} ${
          entry.crimeScreenings.filter((screening) => screening.hits === true)
            .length
        } / ${entry.crimeScreenings.length} ${gT(
          t,
          '.record-entry.suffix-result'
        )}.`}</p>
      )}
    </button>
  );
}

export default function CrimeRecordView({ clientId, isImpersonating, t }) {
  const [screeningRecords, setScreeningRecords] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [selectedScreening, setSelectedScreening] = useState({});
  const [authState] = useContext(AuthContext);

  useEffect(() => {
    const getScreeningRecords = async () => {
      await ScreeningService.getClientCrimeScreenings(clientId)
        .then((response) => setScreeningRecords(response.data))
        .catch(() => {});
    };

    if (clientId) {
      getScreeningRecords();
    }
  }, [clientId]);

  const getDocumentLink = async (resultId) => {
    await ScreeningService.getCrimeDocument(clientId, resultId)
      .then((response) =>
        window.open(response.data, '_blank', 'noopener,noreferrer')
      )
      .catch(() => {});
  };

  const startScreening = async () => {
    setIsScanning(true);
    await ScreeningService.startClientCrimeScreening(clientId)
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        ScreeningService.getClientCrimeScreenings(clientId)
          .then((recordsResponse) => setScreeningRecords(recordsResponse.data))
          .catch(() => {});
        setIsScanning(false);
      });
  };

  const onRecordClick = (record) => {
    if (
      Object.keys(selectedRecord).length > 0 &&
      selectedRecord.id === record.id
    ) {
      setSelectedRecord({});
    } else {
      setSelectedRecord(record);
    }
    setSelectedScreening({});
  };

  const onScreeningClick = (screening) => {
    if (
      Object.keys(selectedScreening).length > 0 &&
      selectedScreening.id === screening.id
    ) {
      setSelectedScreening({});
    } else {
      setSelectedScreening(screening);
    }
  };

  return (
    <DataPanel
      title={gT(t, '.data-panel-title')}
      icon={faCircleQuestion}
      iconStart
      iconHoverContent={
        <p>
          {t(
            'company-info-modules.regulatory.crime-record-view.icon-hover-content',
            { returnObjects: true }
          ).map((key) => (
            <Trans key={key} i18nKey={key} {...{ shouldUnescape, tOptions }} />
          ))}
        </p>
      }
    >
      <div className="crime-record-view">
        <div className="view-column">
          <p className="view-title">{gT(t, '.view-title.scans')}</p>
          <div
            className={`entry-list ${
              screeningRecords.length === 0 ? 'empty-list' : ''
            }`}
          >
            {screeningRecords.length > 0 ? (
              screeningRecords
                .sort(
                  (a, b) =>
                    new Date(b.screeningDate) - new Date(a.screeningDate)
                )
                .map((record) => (
                  <RecordEntry
                    key={record.id}
                    entry={record}
                    onClick={() => onRecordClick(record)}
                    selected={selectedRecord?.id === record.id}
                    t={t}
                  />
                ))
            ) : (
              <p className="no-content-entry">
                {gT(t, '.no-content-entry.no-scans-made')}
              </p>
            )}
          </div>
          <div className="column-footer">
            <XpektorButton
              content={gT(t, '.xpektor-btn.start-screening')}
              onClick={startScreening}
              disabled={
                isImpersonating || !authState.user || authState.user.role < 1
              }
              loading={isScanning}
            />
            <p>
              <i>{gT(t, '.cost-info')}</i>
            </p>
          </div>
        </div>
        {Object.keys(selectedRecord).length > 0 && (
          <div className="view-column">
            <p className="view-title">
              {gT(t, '.view-title.scanned-entities')}
            </p>
            <div
              className={`entry-list ${
                selectedRecord.crimeScreenings.length === 0 ? 'empty-list' : ''
              }`}
            >
              {selectedRecord.crimeScreenings.length > 0 ? (
                selectedRecord.crimeScreenings.map((screening) => {
                  return (
                    <ScreeningEntry
                      key={screening.id}
                      entry={screening}
                      onClick={() => onScreeningClick(screening)}
                      selected={selectedScreening?.id === screening.id}
                      t={t}
                    />
                  );
                })
              ) : (
                <p className="no-content-entry">
                  {gT(t, '.no-content-entry.no-scans-made-error')}
                </p>
              )}
            </div>
          </div>
        )}
        {Object.keys(selectedScreening).length > 0 && (
          <div className="view-column">
            <p className="view-title">{gT(t, '.view-title.results')}</p>
            <div
              className={`entry-list ${
                selectedScreening.results.length === 0 ? 'empty-list' : ''
              }`}
            >
              {selectedScreening.results.length > 0 ? (
                selectedScreening.results.map((result, index) => (
                  <ResultEntry
                    key={result.id}
                    index={index}
                    entry={result}
                    onDownloadClick={() => getDocumentLink(result.id)}
                    t={t}
                  />
                ))
              ) : (
                <p className="no-content-entry">
                  {gT(t, '.no-content-entry.no-results')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </DataPanel>
  );
}
